<template>
  <div class="countdown">
    <span class="countdown-label" style="font-size: 16px;line-height: 1;">剩</span>
    <span v-if="days > 0" class="countdown-item">
      <span class="countdown-value">{{ days | padStart }}</span>
      <span class="countdown-label">天</span>
    </span>
    <span class="countdown-item">
      <span class="countdown-value">{{ hours | padStart }}</span>
      <span class="countdown-label">{{ showText? '时':':' }}</span>
    </span>
    <span class="countdown-item">
      <span class="countdown-value">{{ minutes | padStart }}</span>
      <span class="countdown-label">{{ showText? '分':':' }}</span>
    </span>
    <span class="countdown-item">
      <span class="countdown-value">{{ seconds | padStart }}</span>
      <span class="countdown-label">{{ showText? '秒':'' }}</span>
    </span>
  </div>
</template>

<script>
export default {
  name: 'Countdown',
  
  props: {
    // 支持传递时间戳(毫秒)
    timestamp: {
      type: Number,
      default: 0
    },
    // 或者传递结束时间对象
    endTime: {
      type: Date,
      default: null
    },
    // 或者传递剩余秒数
    secondsRemaining: {
      type: Number,
      default: 0
    },
    // 是否自动开始倒计时
    autoStart: {
      type: Boolean,
      default: true
    },
    // 是否启用本地缓存
    enableCache: {
      type: Boolean,
      default: true
    },
    // 本地缓存的key
    cacheKey: {
      type: String,
      default: 'countdown-timer'
    },
    //是否显示文字
    showText:{
        type:Boolean,
        default:false
    }
  },
  
  data() {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      timer: null,
      totalSeconds: 0,
      usingCache: false, // 新增标志，表示是否正在使用缓存
      cachedEndTime: 0 // 新增，存储缓存中的结束时间
    }
  },
  
  filters: {
    padStart(value) {
      return value.toString().padStart(2, '0')
    }
  },
  
  computed: {
    // 计算结束时间的时间戳
    endTimestamp() {
      if (this.usingCache) {
        return this.cachedEndTime
      } else if (this.timestamp) {
        return this.timestamp
      } else if (this.endTime) {
        return this.endTime.getTime()
      } else if (this.secondsRemaining) {
        return Date.now() + this.secondsRemaining * 1000
      }
      return 0
    }
  },
  
  watch: {
    // 监听props变化重新开始倒计时
    timestamp() {
      this.usingCache = false
      this.startCountdown()
    },
    endTime() {
      this.usingCache = false
      this.startCountdown()
    },
    secondsRemaining() {
      this.usingCache = false
      this.startCountdown()
    }
  },
  
  mounted() {
    if (this.autoStart) {
      this.startCountdown()
    }
  },
  
  beforeDestroy() {
    this.clearCountdown()
  },
  
  methods: {
    // 从本地存储获取缓存的时间
    getCachedTime() {
      if (!this.enableCache) return null
      
      const cachedData = localStorage.getItem(this.cacheKey)
      if (!cachedData) return null
      
      try {
        const { endTimestamp, cachedAt } = JSON.parse(cachedData)
        const now = Date.now()
        
        // 检查缓存是否过期或无效
        if (endTimestamp <= now || endTimestamp <= cachedAt) {
          localStorage.removeItem(this.cacheKey)
          return null
        }
        
        return endTimestamp // 返回结束时间戳而不是剩余时间
      } catch (e) {
        console.error('Countdown: Failed to parse cached data', e)
        return null
      }
    },
    
    // 保存当前倒计时到本地存储
    saveToCache() {
      if (!this.enableCache || !this.endTimestamp) return
      
      const cacheData = {
        endTimestamp: this.endTimestamp,
        cachedAt: Date.now()
      }
      
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData))
    },
    
    // 清除缓存
    clearCache() {
      if (!this.enableCache) return
      localStorage.removeItem(this.cacheKey)
    },
    
    // 开始倒计时
    startCountdown() {
      this.clearCountdown()
      
      // 检查是否有缓存
      if (this.enableCache) {
        const cachedEndTime = this.getCachedTime()
        if (cachedEndTime) {
          this.usingCache = true
          this.cachedEndTime = cachedEndTime
          this.updateCountdown()
          this.timer = setInterval(this.updateCountdown, 1000)
          return
        }
      }
      
      // 没有缓存或缓存无效，使用props的值
      this.usingCache = false
      if (!this.endTimestamp) {
        console.warn('Countdown: No valid end time provided')
        return
      }
      
      // 保存到缓存
      this.saveToCache()
      
      this.updateCountdown()
      this.timer = setInterval(this.updateCountdown, 1000)
    },
    
    // 停止倒计时
    stopCountdown() {
      this.clearCountdown()
      this.clearCache()
    },
    
    // 清除倒计时
    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    // 更新倒计时显示
    updateCountdown() {
      const now = Date.now()
      const diff = this.endTimestamp - now
      
      if (diff <= 0) {
        this.clearCountdown()
        this.clearCache()
        this.days = 0
        this.hours = 0
        this.minutes = 0
        this.seconds = 0
        this.$emit('countdown-end')
        return
      }
      
      // 计算天、时、分、秒
      this.totalSeconds = Math.floor(diff / 1000)
      this.days = Math.floor(this.totalSeconds / (3600 * 24))
      const hoursRemaining = this.totalSeconds % (3600 * 24)
      this.hours = Math.floor(hoursRemaining / 3600)
      const minutesRemaining = hoursRemaining % 3600
      this.minutes = Math.floor(minutesRemaining / 60)
      this.seconds = minutesRemaining % 60
      
      this.$emit('countdown-update', {
        days: this.days,
        hours: this.hours,
        minutes: this.minutes,
        seconds: this.seconds,
        totalSeconds: this.totalSeconds
      })
      
      // 每分钟保存一次到缓存，避免频繁操作localStorage
      if (this.enableCache && this.seconds === 0) {
        this.saveToCache()
      }
    }
  }
}
</script>

<style scoped>
.countdown {
  display: inline-flex;
  align-items: center;
  font-family: Arial, sans-serif;
}

.countdown-item {
  display: inline-flex;
  align-items: center;
  /* margin: 0 4px; */
}

.countdown-value {
  font-size: 16px;
  color: #fff;
}

.countdown-label {
  font-size: 16px;
  color: #fff;
  padding: 0 3px;
}
</style>